import { useGetSuperAdminModules, useGetSuperAdminPackages } from '@agos/api-client/platform'
import { T, formatCount, tt } from '@agos/shared'
import {
  PageHeader,
  ButtonGroup,
  cn,
  EmptyState,
  ReadFailed,
  SearchInput,
  Spinner,
  StatusBadge,
} from '@agos/ui'
import { Lock, ShieldCheck, ToggleLeft } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  categoryLabel,
  enforcementNote,
  enforcementSummary,
  groupByCategory,
  matchesModuleSearch,
  modulesOf,
  packagesByModule,
  packagesOf,
} from './catalog'

/**
 * The module catalogue - §3.3, FB-506.
 *
 * **Read-only in both systems, and that is right**: the catalogue is seeded, not authored. What was
 * wrong is how much of it AGOS drew - the name and an `alwaysOn` note, grouped by the raw category
 * key in alphabetical order. Legacy draws the key, the description (**all 201 modules have one**), the
 * hierarchy, a search over key / name / description, category chips with counts, and a detail panel
 * naming the module's parent, its children and **which packages include it** - which is the question
 * the page exists to answer and the one AGOS could not.
 *
 * `packagesByModule` is derived here from the two payloads already fetched: no endpoint answers it,
 * and asking for one would be a backend change for a join the browser can do.
 */
export function ModulesPage() {
  const modulesQuery = useGetSuperAdminModules()
  const packagesQuery = useGetSuperAdminPackages()
  // §3.107. **Both reads**, because the page draws modules *and* which packages hold them - a
  // module list with no packages beside it is as misleading as an empty one.
  const readFailed = modulesQuery.isError || packagesQuery.isError
  const stillLoading = !readFailed && (modulesQuery.isPending || packagesQuery.isPending)

  const modules = useMemo(() => modulesOf(modulesQuery.data), [modulesQuery.data])
  const packages = useMemo(() => packagesOf(packagesQuery.data), [packagesQuery.data])
  const byModule = useMemo(() => packagesByModule(packages), [packages])
  // §3.72 - the ratio the owner's question #25 was about, derived from the payload rather than
  // stated. Over the whole catalogue, not the filtered view: it is a fact about the product.
  const enforcement = useMemo(() => enforcementSummary(modules), [modules])

  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [chosenKey, setChosenKey] = useState<string | null>(null)

  const counts = useMemo(() => {
    const map = new Map<string, number>()
    for (const module of modules) map.set(module.category, (map.get(module.category) ?? 0) + 1)
    return map
  }, [modules])

  const shown = useMemo(
    () =>
      modules.filter(
        (module) =>
          (category === '' || module.category === category) && matchesModuleSearch(module, search),
      ),
    [modules, category, search],
  )
  const groups = useMemo(() => groupByCategory(shown), [shown])

  const chosen = chosenKey === null ? null : (modules.find((one) => one.key === chosenKey) ?? null)
  const chosenParent =
    chosen?.parentKey === null || chosen?.parentKey === undefined
      ? null
      : (modules.find((one) => one.key === chosen.parentKey) ?? null)
  const chosenChildren = chosen === null ? [] : modules.filter((one) => one.parentKey === chosen.key)
  const chosenPackages = chosen === null ? [] : (byModule.get(chosen.key) ?? [])

  return (
    <main className="space-y-3" data-modules>
      <PageHeader
        title={tt('Moduly')}
        description={tt('Katalóg modulov platformy. Zapínajú sa na franšíze.')}
        icon={<ToggleLeft className="w-5 h-5" />}
      />
      {/* §3.107 - this page read neither `isError` nor `isLoading`, so *Žiadne moduly* appeared
        * while the request was in flight and again when it failed: the module catalogue reporting
        * itself empty because the server did not answer. §3.82 made this argument about seven
        * screens in `apps/super-admin/src/pages` and never looked in this directory. */}
      {readFailed && (
        <ReadFailed marker="modules-error"><T>Katalóg modulov sa nepodarilo načítať.</T></ReadFailed>
      )}
      {stillLoading && (
        <div className="py-10 flex justify-center" data-modules-loading>
          <Spinner />
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        <T>Katalóg všetkých modulov, ktoré možno zapnúť v balíčku.</T>{' '}
        <T>Kliknite na modul pre popis a balíčky, v ktorých je.</T>
      </p>

      {/* §3.72, owner decision #25 - the sentence this page was missing. It offered 210 switches
        * with no way to tell which of them a tenant would feel, so a Basic tier could be sold on
        * the belief that all 210 hold. The figure comes from the payload on every load, because
        * §3.17's written-down "the API enforces 3" was wrong by eighteen four days later. */}
      {enforcementNote(enforcement) !== '' && (
        <p
          className="text-[11px] text-muted-foreground rounded-xl border border-border px-3 py-2"
          data-module-enforcement
        >
          <span className="inline-flex items-center gap-1 font-semibold text-emerald-500">
            <ShieldCheck className="w-3 h-3" />
            {enforcement.enforced}
          </span>{' '}
          <T>{enforcementNote(enforcement)}</T>
        </p>
      )}

      {/* §3.117 - the product's own search box and segmented chooser, not a fifth reimplementation of
        * each. `SearchInput` brings a clear button the hand-rolled box had no room for, and
        * `ButtonGroup` - which is in five manager screens and was in **one** Tower screen - draws the
        * count as a badge rather than in brackets and keeps `aria-pressed` on every chip. */}
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          className="flex-1 min-w-[220px]"
          value={search}
          onChange={setSearch}
          placeholder={tt('Hľadať podľa kľúča, názvu alebo popisu…')}
          label={tt('Hľadať modul')}
          testId="data-module-search"
        />
      </div>

      <div data-module-filters>
        <ButtonGroup
          value={category}
          onChange={(chosen) => setCategory(chosen === category ? '' : chosen)}
          testIdKey="data-module-filter"
          options={[
            { value: '', label: tt('Všetky'), count: modules.length },
            ...[...counts.entries()]
              .sort((left, right) => right[1] - left[1])
              .map(([key, howMany]) => ({
                value: key,
                label: categoryLabel(key),
                count: howMany,
              })),
          ]}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_20rem] gap-3">
        <div className="space-y-2">
          {groups.map((group) => (
            <section
              key={group.category}
              className="rounded-2xl border border-border overflow-hidden"
              data-module-category={group.category}
            >
              <p className="px-3 py-2 bg-muted/40 text-[11px] font-black uppercase tracking-wider">
                {group.label}{' '}
                <span className="text-muted-foreground font-normal">
                  {formatCount(group.modules.length)}
                </span>
              </p>
              <ul className="divide-y divide-border">
                {group.modules.map((module) => (
                  <li key={module.key}>
                    <button
                      type="button"
                      data-module={module.key}
                      onClick={() => setChosenKey(module.key)}
                      className={cn(
                        'w-full text-left px-3 py-2',
                        chosenKey === module.key && 'bg-primary/5',
                        module.parentKey !== null && 'pl-7',
                      )}
                    >
                      <span className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold">{module.name}</span>
                        {module.alwaysOn && (
                          <span
                            className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-amber-500"
                            data-module-always-on
                          >
                            <Lock className="w-2.5 h-2.5" /> <T>vždy</T>
                          </span>
                        )}
                        {/* §3.72 - a different question from `vždy`: not whether a tier may
                          * withhold this, but whether withholding it is felt beyond the sidebar. */}
                        {module.enforced && (
                          <span
                            className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-emerald-500"
                            data-module-enforced
                            title={tt('Server odmietne franšízu, ktorá tento modul nemá')}
                          >
                            <ShieldCheck className="w-2.5 h-2.5" /> <T>API</T>
                          </span>
                        )}
                      </span>
                      <span className="block font-mono text-[11px] text-muted-foreground" data-module-key>
                        {module.key}
                      </span>
                      {module.description !== null && (
                        <span className="block text-xs text-muted-foreground" data-module-description>
                          {module.description}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          {shown.length === 0 && !readFailed && !stillLoading && (
            <div data-no-modules>
              <EmptyState
                icon={<ToggleLeft className="w-12 h-12" />}
                title={modules.length === 0 ? tt('Žiadne moduly') : tt('Nič podľa filtra')}
                description={
                  modules.length === 0
                    ? tt('Katalóg modulov je prázdny.')
                    : tt('Skúste iný okruh alebo vyhľadávanie.')
                }
              />
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-4 self-start">
          {chosen === null ? (
            <p className="text-xs text-muted-foreground rounded-2xl border border-dashed border-border p-4">
              <T>Vyberte modul pre detail.</T>
            </p>
          ) : (
            <div className="rounded-2xl border border-border p-3 space-y-2" data-module-detail={chosen.key}>
              <p className="text-sm font-bold">{chosen.name}</p>
              {/* §3.141 - the row carried both badges and this panel carried neither, while legacy's shows
                * *Vždy zapnuté*. The panel is where somebody reads about a module, and it is reachable
                * **without the row**: §3.113's parent and child links select another module, and a catalogue
                * of 210 scrolls, so arriving at one whose row is off-screen is the normal case.
                *
                * `enforced` is the one that matters and it is AGOS's own (§3.72, owner decision #25): a tier
                * may withhold 210 keys and only 21 are felt beyond the sidebar. Describing a module without
                * it is describing it without the fact that says whether switching it off does anything. */}
              <p className="flex items-center gap-1.5 flex-wrap" data-detail-badges>
                {chosen.alwaysOn && (
                  <span data-detail-always-on>
                    <StatusBadge label={tt('Vždy zapnuté')} color="amber" />
                  </span>
                )}
                {chosen.enforced && (
                  <span data-detail-enforced title={tt('Server odmietne franšízu, ktorá tento modul nemá')}>
                    <StatusBadge label={tt('Vynucuje API')} color="green" />
                  </span>
                )}
                {!chosen.alwaysOn && !chosen.enforced && (
                  <span className="text-[11px] text-muted-foreground" data-detail-sidebar-only>
                    <T>Voliteľný, vynucuje sa len v menu</T>
                  </span>
                )}
              </p>
              <p className="font-mono text-[11px] text-muted-foreground">{chosen.key}</p>
              <p className="text-[11px] text-muted-foreground">
                <T>Kategória</T>: {categoryLabel(chosen.category)}
              </p>
              {chosen.description !== null && <p className="text-xs">{chosen.description}</p>}

              {/* §3.113 - the hierarchy is walkable, as legacy's is. On a catalogue of 201 modules the
                * point of naming a parent and its children is to get to them: reading *Nadradený modul:
                * Rozvoz* and then scrolling a category list to find it is the long way round, and the
                * detail panel is already the place that knows the key. */}
              {chosenParent !== null && (
                <p className="text-[11px]" data-detail-parent={chosenParent.key}>
                  <T>Nadradený modul</T>:{' '}
                  <button
                    type="button"
                    className="font-semibold text-primary"
                    data-detail-parent-open={chosenParent.key}
                    onClick={() => setChosenKey(chosenParent.key)}
                  >
                    {chosenParent.name}
                  </button>
                </p>
              )}
              {chosenChildren.length > 0 && (
                <div className="text-[11px]" data-detail-children={chosenChildren.length}>
                  <T>Podmoduly</T>:
                  <ul className="list-disc pl-4">
                    {chosenChildren.map((child) => (
                      <li key={child.key}>
                        <button
                          type="button"
                          className="text-left text-primary"
                          data-detail-child-open={child.key}
                          onClick={() => setChosenKey(child.key)}
                        >
                          {child.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* The question this page exists to answer, and the one it could not. */}
              <div className="text-[11px]" data-detail-packages={chosenPackages.length}>
                <T>V balíčkoch</T>:{' '}
                {chosenPackages.length === 0 ? (
                  <span className="text-muted-foreground">
                    <T>v žiadnom</T>
                  </span>
                ) : (
                  chosenPackages.map((one) => one.name).join(', ')
                )}
              </div>
            </div>
          )}
        </aside>
      </div>
    </main>
  )
}
